import React, { useState } from 'react'
import styled from 'styled-components'
import { Flex, Text, Button, useWalletModal } from '@pancakeswap/uikit'
import { useTranslation } from 'contexts/Localization'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import useAuth from 'hooks/useAuth'
import { setupNetwork } from 'utils/wallet'
import {
  GRX_CHAIN_ID,
  ROBINHOOD_TESTNET_CHAIN_ID,
  ROBINHOOD_MAINNET_CHAIN_ID,
} from 'config/constants/chains'
import { useRouter } from 'next/router'
import UserMenu from '../Menu/UserMenu'

const HeaderContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border-bottom: 1px solid ${({ theme }) => theme.colors.cardBorder};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  z-index: 999;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 0 16px;
    height: 70px;
  }
`

const LeftSection = styled(Flex)`
  align-items: center;
  gap: 16px;
  flex: 1;
`

const MobileBrandSection = styled(Flex)`
  align-items: center;
  gap: 12px;
  display: none;

  @media (max-width: 768px) {
    display: flex;
  }
`

const LogoIcon = styled.div`
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.primaryBright} 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  color: white;
`

const LogoText = styled(Text)`
  font-size: 20px;
  font-weight: 700;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.primaryBright} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  display: block;

  @media (max-width: 768px) {
    display: none;
  }
`

const PageTitle = styled(Text)`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  display: none;

  @media (max-width: 768px) {
    display: block;
    font-size: 18px;
  }
`

const BreadcrumbContainer = styled(Flex)`
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.textSubtle};
  font-size: 14px;
  display: none;

  @media (max-width: 768px) {
    display: none;
  }
`

const RightSection = styled(Flex)`
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`

const NetworkSelectorContainer = styled.div`
  position: relative;
`

const NetworkMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 220px;
  padding: 8px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.18);
  z-index: 1001;
`

const NetworkOption = styled.button`
  width: 100%;
  border: 0;
  border-radius: 8px;
  padding: 10px 12px;
  background: transparent;
  color: ${({ theme }) => theme.colors.text};
  text-align: left;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundAlt2};
  }
`

const NetworkIndicator = styled(Flex)`
  align-items: center;
  gap: 8px;
  background: ${({ theme }) => theme.colors.backgroundAlt2};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  border-radius: 12px;
  padding: 8px 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  cursor: pointer;
  user-select: none;

  @media (max-width: 768px) {
    padding: 6px 8px;
    font-size: 12px;
    max-width: 120px;
  }

  @media (max-width: 480px) {
    max-width: 100px;
    font-size: 11px;
  }
`

const NetworkDot = styled.div`
  width: 8px;
  height: 8px;
  background: #209950;
  border-radius: 50%;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(32, 153, 80, 0.4);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(32, 153, 80, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(32, 153, 80, 0);
    }
  }
`

const ConnectWalletButton = styled(Button)`
  background: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: 12px;
  font-weight: 600;
  color: white;
  padding: 12px 24px;
  height: auto;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(32, 153, 80, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 14px;
  }
`

const LogoImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  object-fit: cover;
`

const Header: React.FC = () => {
  const { t } = useTranslation()
  const { account, chainId } = useActiveWeb3React()
  const { login, logout } = useAuth()
  const { onPresentConnectModal } = useWalletModal(login, logout, t)
  const router = useRouter()
  const [isNetworkMenuOpen, setIsNetworkMenuOpen] = useState(false)

  const handleNetworkSwitch = async (targetChainId: number) => {
    setIsNetworkMenuOpen(false)

    const switched = await setupNetwork(targetChainId)

    if (!switched) {
      return
    }

    window.setTimeout(() => {
      window.location.reload()
    }, 300)
  }

  const brandName = process.env.NEXT_PUBLIC_BRAND_NAME || 'GrxSwap'
  const logoUrl = process.env.NEXT_PUBLIC_BRAND_LOGO_URL

  const getNetworkName = (id: number | undefined) => {
    switch (id) {
      case ROBINHOOD_TESTNET_CHAIN_ID:
        return 'Robinhood Testnet'
      case ROBINHOOD_MAINNET_CHAIN_ID:
        return 'Robinhood Mainnet'
      case GRX_CHAIN_ID:
      default:
        return 'GRX Chain'
    }
  }

  const handleConnectWallet = () => {
    onPresentConnectModal()
  }

  const handleLogoClick = () => {
    router.push('/')
  }

  return (
    <HeaderContainer>
      <LeftSection>
        {/* Mobile branding - only visible on mobile when sidebar is hidden */}
        <MobileBrandSection onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
          {logoUrl ? (
            <LogoImage src={logoUrl} alt={brandName} />
          ) : (
            <LogoIcon>G</LogoIcon>
          )}
          <LogoText>{brandName}</LogoText>
        </MobileBrandSection>
      </LeftSection>

      <RightSection>
        <NetworkSelectorContainer>
          <NetworkIndicator onClick={() => setIsNetworkMenuOpen((open) => !open)}>
            <NetworkDot />
            <Text fontSize="14px" fontWeight="600">
              {getNetworkName(chainId)}
            </Text>
          </NetworkIndicator>

          {isNetworkMenuOpen && (
            <NetworkMenu>
              <NetworkOption
                type="button"
                onClick={() => handleNetworkSwitch(GRX_CHAIN_ID)}
              >
                GRX Chain Mainnet
              </NetworkOption>

              <NetworkOption
                type="button"
                onClick={() =>
                  handleNetworkSwitch(ROBINHOOD_TESTNET_CHAIN_ID)
                }
              >
                Robinhood Chain Testnet
              </NetworkOption>

              <NetworkOption
                type="button"
                onClick={() =>
                  handleNetworkSwitch(ROBINHOOD_MAINNET_CHAIN_ID)
                }
              >
                Robinhood Chain Mainnet
              </NetworkOption>
            </NetworkMenu>
          )}
        </NetworkSelectorContainer>

        {account ? (
          <UserMenu />
        ) : (
          <ConnectWalletButton onClick={handleConnectWallet}>
            {t('Connect Wallet')}
          </ConnectWalletButton>
        )}
      </RightSection>
    </HeaderContainer>
  )
}

export default Header
